'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import OtpModal from './OtpModal';
import { useAuth } from '@/contexts/AuthContext';

export default function SellerLoginForm() {
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [otpOpen, setOtpOpen] = useState(false);
  const router = useRouter();
  const { signInWithPhone } = useAuth();

  const validatePhone = (v: string) => {
    if (!v) return 'Mobile number is required';
    if (!/^\d{10}$/.test(v)) return 'Enter a valid 10-digit mobile number';
    return '';
  };

  const handleSendOtp = async () => {
    const err = validatePhone(phone);
    setPhoneError(err);
    if (err) return;

    setIsLoading(true);
    try {
      await signInWithPhone(phone);
      toast.success(`OTP sent to +91 ${phone}`);
      setOtpOpen(true);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpVerified = async () => {
    setOtpOpen(false);
    toast.success('Welcome back! Redirecting to your dashboard…');
    await new Promise((r) => setTimeout(r, 400));
    router.push('/seller-dashboard');
  };

  return (
    <>
      <div className="space-y-5">
        {/* Info banner */}
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <div className="flex items-start gap-2">
            <Icon name="InformationCircleIcon" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-700">
              Sellers sign in with their <strong>registered mobile number</strong> via OTP verification.
            </p>
          </div>
        </div>

        {/* Phone Number */}
        <div>
          <label htmlFor="seller-login-phone" className="block text-sm font-600 text-foreground mb-1.5">
            Mobile Number <span className="text-negative">*</span>
          </label>
          <div className="flex gap-2">
            <div className="flex items-center px-3 rounded-lg border border-border bg-muted text-sm text-muted-foreground font-600 select-none">
              +91
            </div>
            <input
              id="seller-login-phone"
              type="tel"
              autoComplete="tel"
              className={`input-field flex-1 ${phoneError ? 'input-error' : ''}`}
              placeholder="98765 43210"
              inputMode="numeric"
              value={phone}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                setPhone(val);
                if (phoneError) setPhoneError('');
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSendOtp(); }}
            />
          </div>
          {phoneError && <p className="text-xs text-negative mt-1">{phoneError}</p>}
          <p className="text-xs text-muted-foreground mt-1.5">
            We&apos;ll send a 6-digit OTP to verify your identity
          </p>
        </div>

        {/* Send OTP Button */}
        <button
          type="button"
          disabled={isLoading}
          onClick={handleSendOtp}
          className="w-full py-2.5 rounded-lg text-sm font-700 btn-primary flex items-center justify-center gap-2"
          style={{ minHeight: '42px' }}
        >
          {isLoading ? (
            <>
              <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
              <span>Sending OTP...</span>
            </>
          ) : (
            <>
              <Icon name="DevicePhoneMobileIcon" size={16} />
              <span>Send OTP via SMS</span>
            </>
          )}
        </button>
      </div>

      <OtpModal
        open={otpOpen}
        phone={phone}
        onClose={() => setOtpOpen(false)}
        onVerified={handleOtpVerified}
        isSeller
      />
    </>
  );
}